/**
 * ReinitializationChecker - Utility for checking if reinitialization should be attempted
 * 
 * Extracted from TypesetterWorkerClient for better modularity and testability.
 * 
 * This utility provides functions for checking if a worker should be reinitialized
 * after a fault, preventing unnecessary reinitialization attempts.
 */

/**
 * Parameters for reinitialization check
 */
export interface ReinitializationCheckParams {
  didReinitAfterFault: boolean;
  hasPageSetup: boolean;
}

/**
 * Check if reinitialization should be attempted
 * 
 * Determines if a worker should be reinitialized after a fault based on
 * whether reinitialization has already been attempted and if page setup is available.
 * 
 * @param params - Reinitialization check parameters
 * @returns True if reinitialization should be attempted, false otherwise
 */
export function shouldAttemptReinitialization(params: ReinitializationCheckParams): boolean {
  const { didReinitAfterFault, hasPageSetup } = params;
  
  // Only attempt reinitialization if:
  // 1. We haven't already tried reinitialization after this fault
  // 2. We have a valid page setup to reinitialize with
  return !didReinitAfterFault && hasPageSetup;
}

/**
 * Log reinitialization failure
 * 
 * @param error - Error that occurred during reinitialization
 */
export function logReinitializationFailure(error: Error): void {
  console.warn('[TypesetterWorkerClient] Reinitialize after fault failed:', error.message);
}

